import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createSchedule,
  deleteSchedule,
  getSchedule,
  getScheduleEvents,
  listSchedules,
  refreshSchedule,
  revalidateSchedule,
} from "./schedules-api";
import { ISSUES_KEY } from "./use-issues";
import type { CreateScheduleRequest } from "../types";

export const SCHEDULES_KEY = ["schedules"] as const;
export const SCHEDULE_KEY = (id: string) => ["schedules", id] as const;
/**
 * Base key for ALL per-schedule events queries (regardless of range). Used
 * both to derive the range-specific key and to invalidate every cached range
 * after a refresh.
 */
export const SCHEDULE_EVENTS_BASE_KEY = (id: string) =>
  ["schedules", id, "events"] as const;
export const SCHEDULE_EVENTS_KEY = (id: string, from: string, to: string) =>
  [...SCHEDULE_EVENTS_BASE_KEY(id), { from, to }] as const;

export function useSchedules() {
  return useQuery({
    queryKey: SCHEDULES_KEY,
    queryFn: listSchedules,
  });
}

export function useSchedule(id: string) {
  return useQuery({
    queryKey: SCHEDULE_KEY(id),
    queryFn: () => getSchedule(id),
    enabled: !!id,
  });
}

/**
 * @param options.enabled additional gate combined with the implicit
 *   id/from/to checks. Use this to delay the fetch until the parent has
 *   resolved the schedule's timezone (otherwise `fromUtc`/`toUtc` are
 *   computed against a fallback TZ and would be 4–5h off).
 */
export function useScheduleEvents(
  id: string,
  fromUtc: string,
  toUtc: string,
  options: { enabled?: boolean } = {},
) {
  const enabledGate = options.enabled ?? true;
  return useQuery({
    queryKey: SCHEDULE_EVENTS_KEY(id, fromUtc, toUtc),
    queryFn: () => getScheduleEvents(id, fromUtc, toUtc),
    enabled: enabledGate && !!id && !!fromUtc && !!toUtc,
  });
}

export function useCreateSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateScheduleRequest) => createSchedule(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SCHEDULES_KEY });
    },
  });
}

export function useDeleteSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteSchedule(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SCHEDULES_KEY });
    },
  });
}

export function useRefreshSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => refreshSchedule(id),
    onSuccess: (schedule) => {
      // A refresh re-derives events, issues, sections, rooms, and subject
      // areas; the older targeted list grew stale every time a new
      // schedule-scoped query was added. Blanket-invalidate everything
      // under the "schedules" root (and the list itself) so the whole page
      // refetches in one shot. Every query key for this feature is shaped
      // either ["schedules"] (list) or ["schedules", id, ...]; the
      // predicate catches both.
      qc.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          if (!Array.isArray(key) || key[0] !== "schedules") return false;
          // The bare list key matches; per-schedule keys match by id.
          return key.length === 1 || key[1] === schedule.id;
        },
      });
    },
  });
}

export function useRevalidateSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => revalidateSchedule(id),
    onSuccess: (schedule) => {
      // Re-running validations only touches the schedule's issue set and the
      // derived issue counts on the schedule/list. The issue list lives under
      // its own ["schedule-issues", id] root (not ["schedules", ...]), so it
      // must be invalidated explicitly alongside the schedule + list.
      qc.invalidateQueries({ queryKey: ISSUES_KEY(schedule.id) });
      qc.invalidateQueries({ queryKey: SCHEDULE_KEY(schedule.id) });
      qc.invalidateQueries({ queryKey: SCHEDULES_KEY });
    },
  });
}
